/**
 * API utilities for WebSocket communication
 */

/**
 * Generate a UUID v4 correlation ID
 * @returns {string}
 */
export function generateCorrelationId() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
}

/**
 * Create a WebSocket message (follows WebSocket guide format)
 * @param {string} service - Service name (e.g., 'AuthenticationService')
 * @param {string} func - Function name (e.g., 'reconnect')
 * @param {object} data - Request data (payload fields)
 * @returns {object} - WebSocket message object
 */
export function createWebSocketMessage(service, func, data = {}) {
  return {
    service,
    function: func,
    correlationID: generateCorrelationId(),
    data: {
      header: {
        service,
        function: func,
        version: '1.0',
        locale: 'en_US'
      },
      ...data
    }
  };
}

/**
 * Create an API request message (for HTTP endpoints)
 * @param {string} service - Service name (e.g., 'ReceiverManagementService')
 * @param {string} func - Function name (e.g., 'addReceiver')
 * @param {object} data - Request data (payload fields at root level)
 * @param {string} token - JWT token (optional)
 * @param {number} userId - User ID (optional)
 * @returns {object} - API request object
 */
export function createApiRequest(service, func, data, token = null, userId = null) {
  // Unified format: header at top level, payload fields alongside
  const request = {
    header: {
      service,
      function: func,
      requestType: 'REQUEST',
      correlationID: generateCorrelationId()
    },
    ...data
  };

  // Add token to header if provided
  if (token) {
    request.header.token = `Bearer ${token}`;
  }

  return request;
}

/**
 * Check if API response is successful
 * @param {object} response - API response
 * @returns {boolean}
 */
export function isApiSuccess(response) {
  if (!response) return false;

  // New format: apiStatus in header
  if (response.header && response.header.apiStatus) {
    return response.header.apiStatus === 'OK';
  }

  // Legacy format: apiStatus at root level
  if (response.apiStatus) {
    return response.apiStatus === 'OK';
  }

  // If no apiStatus field, check for accessToken (auth success)
  if (response.accessToken) {
    return true;
  }

  return false;
}

/**
 * Check if API response is an error
 * @param {object} response - API response
 * @returns {boolean}
 */
export function isApiError(response) {
  if (!response) return false;

  // New format: apiStatus in header
  if (response.header && response.header.apiStatus) {
    return response.header.apiStatus === 'ERROR';
  }

  // Legacy format: apiStatus at root level
  if (response.apiStatus) {
    return response.apiStatus === 'ERROR';
  }

  // Simple error format
  if (response.status === 'error') {
    return true;
  }

  return false;
}

/**
 * Get error messages from API response
 * @param {object} response - API response
 * @returns {string[]} - Array of error messages
 */
export function getApiErrors(response) {
  if (!response) return [];

  // New format: errors in header
  if (response.header && response.header.errors) {
    return response.header.errors.map(err => err.message || err);
  }

  // Legacy format: errors at root level
  if (response.errors) {
    return response.errors.map(err => err.message || err);
  }

  // Check for simple error message
  if (response.message && response.status === 'error') {
    return [response.message];
  }

  return [];
}

/**
 * Get first error message from API response
 * @param {object} response - API response
 * @returns {string|null} - First error message or null
 */
export function getFirstApiError(response) {
  const errors = getApiErrors(response);
  return errors.length > 0 ? errors[0] : null;
}
